import { useState, useCallback } from 'react';
import { ChevronDown, ChevronUp, Settings, LayoutList, LayoutGrid } from 'lucide-react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { DEFAULT_VOICES } from '@/constants/defaultVoices';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { ISettingsBarProps, ILayoutToggleProps } from '@/@types/propertySceneEditor';
import type { IClonedVoice, IDefaultVoice } from '@/@types/voices';

const DURATION_OPTIONS = [
  { value: 30, label: '30 seconds' },
  { value: 45, label: '45 seconds' },
  { value: 60, label: '1 minute' },
  { value: 90, label: '1.5 minutes' },
  { value: 120, label: '2 minutes' },
];

export function SettingsBar({
  selectedVoiceId,
  selectedDuration,
  selectedAspectRatio,
  onVoiceChange,
  onDurationChange,
  onAspectRatioChange,
}: ISettingsBarProps) {
  const [isOpen, setIsOpen] = useState(false);

  const clonedVoices = useQuery(api.voices.getActiveVoices);

  const getVoiceDisplayName = useCallback((): string => {
    const clonedVoice = clonedVoices?.find(
      (voice: IClonedVoice) => voice.elevenLabsVoiceId === selectedVoiceId
    );
    if (clonedVoice) return clonedVoice.name;

    const defaultVoice = DEFAULT_VOICES.find(
      (voice: IDefaultVoice) => voice.id === selectedVoiceId
    );
    if (defaultVoice) return defaultVoice.name;

    return 'Select voice';
  }, [selectedVoiceId, clonedVoices]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg bg-muted/30">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">Video Settings</span>
              <span className="text-xs text-muted-foreground">
                ({getVoiceDisplayName()}, {selectedDuration}s, {selectedAspectRatio})
              </span>
            </div>
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="grid grid-cols-3 gap-4 p-4 pt-2 border-t">
            <div className="space-y-2">
              <Label htmlFor="voice-select" className="text-xs">
                Voice
              </Label>
              <Select value={selectedVoiceId} onValueChange={onVoiceChange}>
                <SelectTrigger id="voice-select" className="h-9">
                  <SelectValue placeholder="Select voice" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {clonedVoices && clonedVoices.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                        Your Cloned Voices
                      </div>
                      {clonedVoices.map((voice: IClonedVoice) => (
                        <SelectItem key={voice._id} value={voice.elevenLabsVoiceId}>
                          {voice.name}
                        </SelectItem>
                      ))}
                      <div className="my-1 border-t" />
                    </>
                  )}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    Pre-made Voices
                  </div>
                  {DEFAULT_VOICES.map(voice => (
                    <SelectItem key={voice.id} value={voice.id}>
                      {voice.name} ({voice.accent})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration-select" className="text-xs">
                Duration
              </Label>
              <Select
                value={selectedDuration.toString()}
                onValueChange={value => onDurationChange(parseInt(value, 10))}
              >
                <SelectTrigger id="duration-select" className="h-9">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Aspect Ratio</Label>
              <div className="flex gap-2">
                <Button
                  variant={selectedAspectRatio === '16:9' ? 'secondary' : 'outline'}
                  size="sm"
                  className="flex-1 h-9"
                  onClick={() => onAspectRatioChange('16:9')}
                >
                  16:9
                </Button>
                <Button
                  variant={selectedAspectRatio === '9:16' ? 'secondary' : 'outline'}
                  size="sm"
                  className="flex-1 h-9"
                  onClick={() => onAspectRatioChange('9:16')}
                >
                  9:16
                </Button>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export function LayoutToggle({ layout, onLayoutChange }: ILayoutToggleProps) {
  return (
    <div
      className="flex items-center gap-1 p-1 bg-muted rounded-lg"
      role="group"
      aria-label="Scene layout options"
    >
      <Button
        variant={layout === 'horizontal' ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => onLayoutChange('horizontal')}
        aria-label="Compact view"
        aria-pressed={layout === 'horizontal'}
      >
        <LayoutList className="h-4 w-4" />
      </Button>
      <Button
        variant={layout === 'vertical' ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => onLayoutChange('vertical')}
        aria-label="Detailed view"
        aria-pressed={layout === 'vertical'}
      >
        <LayoutGrid className="h-4 w-4" />
      </Button>
    </div>
  );
}
